<template>
  <div class="mobile-surrender-create">

    <!-- Header fijo -->
    <div class="msc-header sticky-top bg-dark text-white p-3">
      <b-row align-v="center">
        <b-col cols="2">
          <b-button variant="link" @click="$router.go(-1)" class="text-white p-0">
            <b-icon icon="arrow-left" font-scale="1.5"></b-icon>
          </b-button>
        </b-col>
        <b-col cols="8" class="text-center">
          <h5 class="mb-0">Rendir Cobranzas</h5>
          <small class="text-white-50" v-if="!loading && pendingPayments.length">
            {{ pendingPayments.length }} cobranza{{ pendingPayments.length !== 1 ? 's' : '' }} pendiente{{ pendingPayments.length !== 1 ? 's' : '' }}
          </small>
        </b-col>
        <b-col cols="2" class="text-right">
          <b-button variant="link" class="text-white p-0" @click="loadPayments()" :disabled="loading">
            <b-icon icon="arrow-clockwise" font-scale="1.2"></b-icon>
          </b-button>
        </b-col>
      </b-row>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" style="width: 2.5rem; height: 2.5rem;"></b-spinner>
      <p class="mt-3 text-muted">Cargando cobranzas...</p>
    </div>

    <div v-else class="p-3 pb-5">

      <!-- Rendiciones para revisar -->
      <div v-if="surrendersToReview.length" class="mb-4">
        <div class="msc-section-label mb-2">
          <b-icon icon="exclamation-circle-fill" class="mr-1 text-danger"></b-icon>
          Para revisar
        </div>
        <b-card no-body v-for="s in surrendersToReview" :key="s.id"
                class="mb-2 border-danger"
                style="cursor:pointer;"
                @click="editSurrender(s)">
          <b-card-body class="py-2 px-3">
            <div class="d-flex align-items-center justify-content-between">
              <div>
                <div class="font-weight-bold" style="font-size:0.9rem;">
                  Rendición #{{ s.id }} — {{ formatDate(s.date) }}
                </div>
                <small class="text-muted">
                  {{ s.detail ? s.detail.length : 0 }} cobranza{{ s.detail && s.detail.length !== 1 ? 's' : '' }}
                  <span v-if="s.observations"> · {{ s.observations }}</span>
                </small>
              </div>
              <div class="d-flex align-items-center">
                <b-badge variant="danger" class="mr-2">Revisar</b-badge>
                <b-icon icon="chevron-right" variant="danger"></b-icon>
              </div>
            </div>
          </b-card-body>
        </b-card>
      </div>

      <!-- Sin cobranzas pendientes -->
      <div v-if="!pendingPayments.length" class="text-center py-5">
        <b-icon icon="check-circle-fill" font-scale="4" variant="success" class="mb-3"></b-icon>
        <h5 class="text-muted">Sin cobranzas pendientes</h5>
        <p class="text-muted small">Todas las cobranzas ya fueron rendidas.</p>
        <b-button variant="outline-secondary" @click="$router.go(-1)">
          <b-icon icon="arrow-left" class="mr-1"></b-icon>
          Volver
        </b-button>
      </div>

      <div v-else>

        <!-- Observaciones -->
        <b-card class="mb-4">
          <b-form-group label="Observaciones (opcional)" label-size="sm" class="mb-0">
            <app-textarea
              v-model="form.observations"
              rows="3"
              placeholder="Agregar observaciones...">
            </app-textarea>
          </b-form-group>
        </b-card>

        <!-- Listado de cobranzas -->
        <div class="msc-section-label mb-2">
          <b-icon icon="receipt" class="mr-1"></b-icon>
          Cobranzas a rendir
        </div>

        <b-card no-body class="mb-4">
          <!-- Cabecera de tabla -->
          <div class="msc-table-header d-flex px-3 py-2">
            <div class="flex-grow-1 small font-weight-bold text-muted">Cliente</div>
            <div class="msc-col-amount small font-weight-bold text-muted text-right">Efectivo</div>
            <div class="msc-col-amount small font-weight-bold text-muted text-right">Cheque</div>
            <div style="width:32px;"></div>
          </div>

          <!-- Filas -->
          <div v-for="(payment, index) in pendingPayments"
               :key="payment.id"
               class="msc-payment-row d-flex align-items-center px-3 py-2"
               :class="{ 'border-top': index > 0 }">
            <div class="flex-grow-1 pr-2">
              <div class="font-weight-bold" style="font-size:0.9rem;">
                {{ payment.customer ? payment.customer.name : '—' }}
              </div>
              <small class="text-muted">{{ formatDate(payment.date) }}</small>
            </div>
            <!-- Efectivo -->
            <div class="msc-col-amount text-right">
              <span v-if="getEfectivo(payment) > 0" class="text-success font-weight-bold" style="font-size:0.9rem;">
                $ {{ formatMoney(getEfectivo(payment)) }}
              </span>
              <span v-else class="text-muted">—</span>
            </div>
            <!-- Cheque -->
            <div class="msc-col-amount text-right">
              <span v-if="getCheque(payment) > 0" class="font-weight-bold" style="font-size:0.9rem;">
                $ {{ formatMoney(getCheque(payment)) }}
                <b-icon
                  icon="info-circle"
                  class="text-primary ml-1"
                  style="cursor:pointer; font-size:0.85rem;"
                  v-b-tooltip.html.left="getCheckTooltip(payment)">
                </b-icon>
              </span>
              <span v-else class="text-muted">—</span>
            </div>
            <!-- Editar -->
            <div style="width:32px;" class="text-right pl-1">
              <b-button variant="link" class="p-0" @click="openEditPayment(payment)">
                <b-icon icon="pencil-square" variant="primary" font-scale="1.1"></b-icon>
              </b-button>
            </div>
          </div>
        </b-card>

        <!-- Resumen de totales -->
        <div class="msc-section-label mb-2">
          <b-icon icon="calculator" class="mr-1"></b-icon>
          Resumen
        </div>

        <b-card class="mb-4">
          <b-row class="mb-2 align-items-center">
            <b-col cols="6" class="text-muted small">Cobranzas</b-col>
            <b-col cols="6" class="text-right">
              <b-badge variant="secondary" pill>{{ pendingPayments.length }}</b-badge>
            </b-col>
          </b-row>
          <b-row class="mb-2 align-items-center">
            <b-col cols="6" class="text-muted small">
              <b-icon icon="cash" class="mr-1 text-success"></b-icon>
              Efectivo
            </b-col>
            <b-col cols="6" class="text-right font-weight-bold text-success">
              $ {{ formatMoney(totalEfectivo) }}
            </b-col>
          </b-row>
          <b-row class="mb-2 align-items-center">
            <b-col cols="6" class="text-muted small">
              <b-icon icon="credit-card" class="mr-1 text-primary"></b-icon>
              Cheques
            </b-col>
            <b-col cols="6" class="text-right font-weight-bold text-primary">
              $ {{ formatMoney(totalCheque) }}
            </b-col>
          </b-row>
          <hr class="my-2">
          <b-row class="align-items-center">
            <b-col cols="5" class="font-weight-bold">Total</b-col>
            <b-col cols="7" class="text-right">
              <span class="h5 mb-0 font-weight-bold text-dark">$ {{ formatMoney(totalGeneral) }}</span>
            </b-col>
          </b-row>
        </b-card>


      </div>
    </div>

    <!-- Modal edición de cobranza -->
    <b-modal
      v-model="editModal.active"
      title="Editar cobranza"
      centered
      hide-footer
      header-bg-variant="dark"
      header-text-variant="white"
      :key="editModal.key">

      <div v-if="editModal.payment">
        <div class="mb-3">
          <strong>{{ editModal.payment.customer ? editModal.payment.customer.name : '—' }}</strong>
          <div class="text-muted small">{{ formatDate(editModal.payment.date) }}</div>
        </div>

        <!-- Agregar ítem -->
        <div class="d-flex align-items-center mb-2">
          <b-form-select
            v-model="editModal.newItem.type"
            :options="typeOptions"
            style="flex:1; margin-right:8px;">
          </b-form-select>
          <b-form-input
            v-model="editModal.newItem.amount"
            type="number"
            placeholder="Monto"
            style="flex:1; margin-right:8px;">
          </b-form-input>
          <b-button
            variant="primary"
            size="sm"
            :disabled="!editModal.newItem.amount || parseFloat(editModal.newItem.amount) <= 0 || (editModal.newItem.type === 'Cheque' && (!editModal.newItem.check.type || !editModal.newItem.check.number || !editModal.newItem.check.cuit))"
            @click="addEditItem()">
            <b-icon icon="plus"></b-icon>
          </b-button>
        </div>

        <!-- Datos cheque -->
        <div v-if="editModal.newItem.type === 'Cheque'" class="border rounded p-3 mb-3 bg-light">
          <b-row class="mb-2">
            <b-col cols="6">
              <b-form-select v-model="editModal.newItem.check.type"
                :options="[{value:'de_terceros',text:'Terceros'},{value:'propio',text:'Propio'}]"
                size="sm">
              </b-form-select>
            </b-col>
            <b-col cols="6">
              <b-form-input v-model="editModal.newItem.check.bank" placeholder="Banco" size="sm"></b-form-input>
            </b-col>
          </b-row>
          <b-row class="mb-2">
            <b-col cols="6">
              <b-form-input v-model="editModal.newItem.check.number" placeholder="Nro cheque" size="sm"></b-form-input>
            </b-col>
            <b-col cols="6">
              <b-form-input v-model="editModal.newItem.check.cuit" placeholder="CUIT" size="sm"></b-form-input>
            </b-col>
          </b-row>
          <b-row>
            <b-col cols="12">
              <b-form-input v-model="editModal.newItem.check.date_movement" type="date" size="sm"></b-form-input>
            </b-col>
          </b-row>
          <div v-if="!editModal.newItem.check.number || !editModal.newItem.check.cuit"
               class="text-danger small mt-2">
            Nro cheque y CUIT son obligatorios.
          </div>
        </div>

        <!-- Lista de ítems -->
        <div v-if="editModal.items.length" class="mb-3 border rounded">
          <div v-for="(item, idx) in editModal.items" :key="idx"
               class="d-flex align-items-center px-3 py-2"
               :class="idx < editModal.items.length - 1 ? 'border-bottom' : ''">
            <div class="flex-grow-1">
              <span class="font-weight-bold">{{ item.type }}</span>
              <span v-if="item.type === 'Cheque' && item.check && item.check.bank" class="text-muted small ml-1">
                — {{ item.check.bank }} #{{ item.check.number }}
              </span>
            </div>
            <span class="font-weight-bold mr-2">$ {{ formatMoney(item.amount) }}</span>
            <b-button variant="link" class="text-danger p-0" @click="editModal.items.splice(idx, 1)">
              <b-icon icon="trash"></b-icon>
            </b-button>
          </div>
        </div>
        <b-alert v-else show variant="warning" class="mb-3 py-2 small">
          Sin ítems. Agregá al menos uno.
        </b-alert>

        <!-- Total -->
        <div class="d-flex justify-content-between align-items-center mb-3">
          <span class="text-muted small">Total cobranza:</span>
          <span class="font-weight-bold text-primary" style="font-size:1.05rem">
            $ {{ formatMoney(editModal.items.reduce(function(s, i) { return s + parseFloat(i.amount || 0) }, 0)) }}
          </span>
        </div>

        <div class="d-flex justify-content-end">
          <b-button variant="secondary" class="mr-2" @click="editModal.active = false">Cancelar</b-button>
          <b-button variant="dark" :disabled="editModal.saving || !editModal.items.length" @click="saveEditPayment()">
            <b-spinner small v-if="editModal.saving" class="mr-1"></b-spinner>
            Guardar cambios
          </b-button>
        </div>
      </div>
    </b-modal>

    <!-- Footer fijo con botón de envío -->
    <div class="fixed-bottom msc-footer bg-white border-top p-3" v-if="!loading && pendingPayments.length">
      <b-button
        variant="success"
        block
        size="lg"
        @click="confirmSubmit()"
        :disabled="submitting">
        <b-spinner small v-if="submitting" class="mr-1"></b-spinner>
        <b-icon icon="check2-circle" class="mr-1" v-else></b-icon>
        Rendir {{ pendingPayments.length }} cobranza{{ pendingPayments.length !== 1 ? 's' : '' }}
      </b-button>
    </div>

  </div>
</template>

<script>
  import serviceAPI from './services'
  import PrinterServices from '@/components/inc/printer/services'
  import Error from '@/handler/error'
  import Helper from '@/handler/helper'
  import Profiles from '@/config/profiles'
  import Modules from '@/config/modules'
  import moment from 'moment'

  export default {
    data() {
      return {
        access: {
          module_id: Modules.INFORME_COBRANZAS,
          profile_id: Profiles.PERSONAL,
          view_reference: 'mobileSurrenderCreate',
          elements: {}
        },
        loading: false,
        submitting: false,
        allPayments: [],
        surrendersToReview: [],
        form: {
          observations: '',
        },
        typeOptions: [
          { value: 'Efectivo', text: 'Efectivo' },
          { value: 'Cheque',   text: 'Cheque'   },
        ],
        editModal: {
          active: false,
          key: 0,
          saving: false,
          payment: null,
          items: [],
          newItem: {
            type: 'Efectivo',
            amount: '',
            check: { type: 'de_terceros', bank: '', number: '', cuit: '', date_movement: '', observations: '' }
          },
        },
      }
    },
    computed: {
      pendingPayments() {
        return this.allPayments.filter(p => !p.surrender_detail_id)
      },
      totalEfectivo() {
        return this.pendingPayments.reduce((sum, p) => sum + this.getEfectivo(p), 0)
      },
      totalCheque() {
        return this.pendingPayments.reduce((sum, p) => sum + this.getCheque(p), 0)
      },
      totalGeneral() {
        return this.totalEfectivo + this.totalCheque
      }
    },
    mounted() {
      Helper.hasAccessView(this.access)
      this.loadPayments()
      this.loadSurrenderToReview()
    },
    methods: {
      loadPayments() {
        const seller = Helper.getSeller()
        if (!seller) return

        this.loading = true
        serviceAPI.obtenerReportsBySeller({ sellers_id: seller.id })
          .then(response => {
            this.allPayments = response.data
            this.loading = false
          })
          .catch(error => {
            this.loading = false
            this.$awn.alert(Error.showError(error))
          })
      },

      loadSurrenderToReview() {
        const seller = Helper.getSeller()
        if (!seller) return

        const today = moment().format('YYYY-MM-DD')
        serviceAPI.filtrarReportSurrender({
          sellers: [{ code: seller.id }],
          status: { code: 'Revisar' },
          date_start: '2000-01-01',
          date_end: today,
        })
          .then(response => {
            this.surrendersToReview = response.data
          })
          .catch(() => {})
      },

      editSurrender(surrender) {
        this.$router.push({
          name: 'ReportsPaymentStaffMobileSurrenderEdit',
          params: { surrenderId: surrender.id }
        })
      },

      getEfectivo(payment) {
        if (!payment.items) return 0
        return payment.items
          .filter(i => i.type === 'Efectivo')
          .reduce((sum, i) => sum + parseFloat(i.amount || 0), 0)
      },

      getCheque(payment) {
        if (!payment.items) return 0
        return payment.items
          .filter(i => i.type === 'Cheque')
          .reduce((sum, i) => sum + parseFloat(i.amount || 0), 0)
      },

      getCheckTooltip(payment) {
        if (!payment.items) return ''
        const checks = payment.items.filter(i => i.type === 'Cheque')
        if (!checks.length) return ''
        return checks.map(c => {
          const parts = []
          if (c.check_bank) parts.push('<b>Banco:</b> ' + c.check_bank)
          if (c.check_number) parts.push('<b>Nro:</b> ' + c.check_number)
          if (c.check_cuit) parts.push('<b>CUIT:</b> ' + c.check_cuit)
          if (c.check_type) parts.push(c.check_type === 'de_terceros' ? 'Terceros' : 'Propio')
          parts.push('<b>$ ' + this.formatMoney(c.amount) + '</b>')
          return parts.join(' · ')
        }).join('<br>')
      },

      formatMoney(value) {
        const n = parseFloat(value || 0)
        return isNaN(n) ? '0,00' : n.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      },

      formatDate(date) {
        return moment(date).format('DD/MM/YYYY')
      },

      confirmSubmit() {
        this.$bvModal.msgBoxConfirm(
          '¿Confirma rendir ' + this.pendingPayments.length + ' cobranza' + (this.pendingPayments.length !== 1 ? 's' : '') + ' por un total de $ ' + this.formatMoney(this.totalGeneral) + '?',
          {
            title: 'Confirmar rendición',
            okTitle: 'Confirmar',
            cancelTitle: 'Cancelar',
            okVariant: 'success',
            headerBgVariant: 'dark',
            headerTextVariant: 'white',
            centered: true,
          }
        ).then(confirmed => {
          if (confirmed) this.submit()
        })
      },

      submit() {
        const seller = Helper.getSeller()
        this.submitting = true

        const data = {
          sellers_id: seller.id,
          date: moment().format('YYYY-MM-DD'),
          observations: this.form.observations,
          payments: this.pendingPayments.map(p => p.id),
          read: false,
        }

        serviceAPI.agregarSurrender(data)
          .then(response => {
            this.submitting = false
            const surrenderId = response.data.id
            this.printSurrender(surrenderId)
            this.$router.push({ name: 'ReportsPaymentStaffCrudSurrenderSeller' })
          })
          .catch(error => {
            this.submitting = false
            this.$awn.alert(Error.showError(error))
          })
      },

      openEditPayment(payment) {
        this.editModal.payment = payment
        this.editModal.items = (payment.items || []).map(i => ({
          type: i.type,
          amount: parseFloat(i.amount || 0),
          check: {
            type:          i.check_type          || 'de_terceros',
            bank:          i.check_bank          || '',
            number:        i.check_number        || '',
            cuit:          i.check_cuit          || '',
            date_movement: i.check_date_movement || '',
            observations:  i.check_observations  || '',
          }
        }))
        this.editModal.newItem = {
          type: 'Efectivo',
          amount: '',
          check: { type: 'de_terceros', bank: '', number: '', cuit: '', date_movement: '', observations: '' }
        }
        this.editModal.key = Math.random()
        this.editModal.active = true
      },

      addEditItem() {
        const amt = parseFloat(this.editModal.newItem.amount)
        if (!amt || amt <= 0) return
        this.editModal.items.push({
          type: this.editModal.newItem.type,
          amount: amt,
          check: { ...this.editModal.newItem.check },
        })
        this.editModal.newItem.amount = ''
        this.editModal.newItem.check = { type: 'de_terceros', bank: '', number: '', cuit: '', date_movement: '', observations: '' }
      },

      saveEditPayment() {
        if (!this.editModal.items.length) return
        this.editModal.saving = true

        const payload = {
          detail: this.editModal.items.map(i => ({
            type: i.type,
            amount: i.amount,
            check: i.check,
          }))
        }

        serviceAPI.actualizarItemsCobranza(this.editModal.payment.id, payload)
          .then(() => {
            this.editModal.saving = false
            this.editModal.active = false
            this.$awn.success('Cobranza actualizada')
            this.loadPayments()
          })
          .catch(error => {
            this.editModal.saving = false
            this.$awn.alert(Error.showError(error))
          })
      },

      printSurrender(id) {
        PrinterServices.generate({
          reference: 'reports-payment-surrender',
          regID: id,
          staff_id: Helper.getStaffId(),
        }).then(response => {
          window.open(
            response.data,
            'Rendicion',
            'toolbar=no,scrollbars=yes,location=no,statusbar=no,menubar=no,resizable=1,width=1000,height=800,left=200,top=80'
          )
        }).catch(error => {
          this.$awn.alert(Error.showError(error))
        })
      }
    }
  }
</script>

<style scoped>
  .mobile-surrender-create {
    min-height: 100vh;
    background-color: #f5f5f5;
    padding-bottom: 80px;
  }

  .msc-header {
    z-index: 500;
  }

  .msc-section-label {
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #6c757d;
  }

  .msc-table-header {
    background-color: #f8f9fa;
    border-bottom: 1px solid #dee2e6;
  }

  .msc-payment-row {
    background-color: #ffffff;
    transition: background-color 0.15s;
  }

  .msc-payment-row:active {
    background-color: #f0f0f0;
  }

  .msc-col-amount {
    min-width: 110px;
    padding-left: 16px;
  }

  .msc-footer {
    z-index: 1030;
  }
</style>
